(() => {
  const currentScript = document.currentScript;
  const GSAP_SRC = currentScript?.dataset.gsapSrc || '';
  const SCROLL_TRIGGER_SRC = currentScript?.dataset.scrolltriggerSrc || '';
  const STORMCREW_TRIGGER_PREFIX = 'stormcrew-';

  let loadPromise;
  let matchMediaContext;
  let cleanupHandlers = [];
  let initTimer;

  const selectors = {
    reveal: [
      '.sc-kickers',
      '.sc-hero__copy > p',
      '.sc-hero-card',
      '.sc-section-header',
      '.sc-path-card',
      '.sc-product-card',
      '.sc-feature-band__copy > *',
      '.sc-feature-band__image',
      '.sc-image-tile',
      '.sc-split__image',
      '.sc-split__copy > *',
      '#MainContent .hero__content-wrapper > *',
      '#MainContent .announcement-bar__slide',
      '#MainContent marquee-component .marquee__repeated-items > *',
      '#MainContent .section-resource-list__header',
      '#MainContent .section-resource-list__content > *',
      '#MainContent .resource-list__item',
      '#MainContent .media-with-content .media-block',
      '#MainContent .media-with-content__content > *',
      '.product-card',
      '.collection-card',
      '.resource-card',
      '.blog-post-card',
      '.product-information__grid',
      '.product-information [data-product-form]',
      '.main-page-title',
      '.rte > *',
    ].join(','),
    textReveal: [
      '.sc-hero h1',
      '.sc-hero h2',
      '.sc-section-header h2',
      '.sc-feature-band__copy h2',
      '.sc-split__copy h2',
      '#MainContent .hero h1',
      '#MainContent .hero h2',
      '#MainContent .section-resource-list h1',
      '#MainContent .section-resource-list h2',
      '#MainContent .section-resource-list h3',
      '#MainContent .media-with-content h1',
      '#MainContent .media-with-content h2',
      '#MainContent .media-with-content h3',
      '#MainContent .main-page-title',
      '#MainContent .collection-title',
      '#MainContent .product-information .product-title',
      '#MainContent .rte h1',
      '#MainContent .rte h2',
    ].join(','),
    hoverCards: [
      '.sc-hero-card',
      '.sc-path-card',
      '.sc-product-card',
      '.sc-feature-band__image',
      '.sc-image-tile',
      '.sc-split__image',
      '.product-card',
      '.collection-card',
      '.resource-card',
      '.blog-post-card',
    ].join(','),
    buttons: '.button, .button-secondary, .shopify-payment-button__button, .sc-button',
    images: '.sc-hero-card img, .sc-product-card img, .sc-feature-band__image img, .sc-image-tile img, .sc-split__image img, #MainContent .hero img, #MainContent .media-with-content img, .product-card img, .collection-card img, .resource-card img',
    rails: [
      '.sc-paths',
      '.sc-benefit-strip',
      '.sc-product-grid',
      '#MainContent .resource-list__carousel',
      '#MainContent .product-list',
      '#MainContent .collection-list',
    ].join(','),
  };

  const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

  const uniqueElements = (selector, root = document) => {
    return Array.from(root.querySelectorAll(selector)).filter((element, index, list) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.closest('[hidden], [aria-hidden="true"], noscript')) return false;
      return list.indexOf(element) === index;
    });
  };

  const getMaxScrollLeft = (element) => Math.max(0, element.scrollWidth - element.clientWidth);

  const isAlreadyInView = (element, offset = 0.92) => {
    const rect = element.getBoundingClientRect();
    return rect.top < window.innerHeight * offset && rect.bottom > 0;
  };

  const animateScrollLeft = (gsap, element, left, vars = {}) => {
    const target = clamp(left, 0, getMaxScrollLeft(element));
    if (!gsap) {
      element.scrollTo?.({ left: target, behavior: vars.smooth === false ? 'auto' : 'smooth' });
      return;
    }

    gsap.to(element, {
      scrollLeft: target,
      duration: vars.duration ?? 0.48,
      ease: vars.ease || 'power3.out',
      overwrite: 'auto',
    });
  };

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);

      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
          return;
        }

        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.stormcrewGsap = 'true';
      script.addEventListener(
        'load',
        () => {
          script.dataset.loaded = 'true';
          resolve();
        },
        { once: true }
      );
      script.addEventListener('error', reject, { once: true });
      document.head.append(script);
    });

  const ensureGsap = () => {
    if (window.gsap && window.ScrollTrigger) return Promise.resolve();
    if (!GSAP_SRC || !SCROLL_TRIGGER_SRC) return Promise.reject(new Error('GSAP theme assets are unavailable.'));

    loadPromise ||= loadScript(GSAP_SRC).then(() => loadScript(SCROLL_TRIGGER_SRC));
    return loadPromise;
  };

  const cleanup = () => {
    cleanupHandlers.forEach((handler) => handler());
    cleanupHandlers = [];

    if (matchMediaContext) {
      matchMediaContext.revert();
      matchMediaContext = null;
    }

    if (window.ScrollTrigger) {
      window.ScrollTrigger.getAll()
        .filter((trigger) => String(trigger.vars.id || '').startsWith(STORMCREW_TRIGGER_PREFIX))
        .forEach((trigger) => trigger.kill());
    }
  };

  const setWillChange = (gsap, elements, value = 'transform, opacity') => {
    if (!elements.length) return;
    gsap.set(elements, { willChange: value });
    cleanupHandlers.push(() => gsap.set(elements, { clearProps: 'willChange' }));
  };

  const initReveal = (gsap, ScrollTrigger, isMobile) => {
    const revealItems = uniqueElements(selectors.reveal).filter((element) => {
      if (element.matches('h1, h2, h3, h4, .main-page-title, .collection-title, .product-title')) return false;
      if (isAlreadyInView(element, isMobile ? 1 : 0.82)) return false;
      return !element.closest('header, footer, .predictive-search, dialog, modal-dialog');
    });

    if (!revealItems.length) return;

    setWillChange(gsap, revealItems);
    gsap.set(revealItems, { autoAlpha: 0, y: isMobile ? 10 : 24 });

    ScrollTrigger.batch(revealItems, {
      id: `${STORMCREW_TRIGGER_PREFIX}reveal`,
      interval: 0.08,
      batchMax: isMobile ? 4 : 7,
      start: 'top 88%',
      once: true,
      onEnter: (batch) => {
        gsap.to(batch, {
          autoAlpha: 1,
          y: 0,
          duration: isMobile ? 0.42 : 0.68,
          ease: 'power2.out',
          stagger: isMobile ? 0.045 : 0.07,
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility,willChange',
        });
      },
    });
  };

  const initSpotTextReveal = (gsap, ScrollTrigger, isMobile) => {
    const headings = uniqueElements(selectors.textReveal).filter((element) => {
      if (isAlreadyInView(element, isMobile ? 1 : 0.82)) return false;
      return !element.closest('header, footer, .predictive-search, dialog, modal-dialog');
    });

    if (!headings.length) return;

    setWillChange(gsap, headings);
    gsap.set(headings, {
      autoAlpha: 0,
      y: isMobile ? 12 : 34,
      clipPath: isMobile ? 'inset(0 0 10% 0)' : 'inset(0 0 18% 0)',
    });

    ScrollTrigger.batch(headings, {
      id: `${STORMCREW_TRIGGER_PREFIX}spot-text`,
      interval: 0.08,
      batchMax: isMobile ? 3 : 5,
      start: 'top 90%',
      once: true,
      onEnter: (batch) => {
        gsap.to(batch, {
          autoAlpha: 1,
          y: 0,
          clipPath: 'inset(0 0 0% 0)',
          duration: isMobile ? 0.48 : 0.82,
          ease: 'power3.out',
          stagger: isMobile ? 0.045 : 0.07,
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility,clipPath,willChange',
        });
      },
    });
  };

  const initSpotImageReveal = (gsap, ScrollTrigger, isMobile) => {
    const images = uniqueElements(selectors.images)
      .filter((image) => !image.closest('header, footer, .predictive-search, dialog, modal-dialog'))
      .filter((image) => !isAlreadyInView(image, isMobile ? 1 : 0.86))
      .slice(0, isMobile ? 18 : 34);

    if (!images.length) return;

    setWillChange(gsap, images, 'transform');
    gsap.set(images, { scale: isMobile ? 1.018 : 1.055, autoAlpha: isMobile ? 0.96 : 1 });

    ScrollTrigger.batch(images, {
      id: `${STORMCREW_TRIGGER_PREFIX}spot-image`,
      interval: 0.1,
      batchMax: isMobile ? 4 : 8,
      start: 'top 92%',
      once: true,
      onEnter: (batch) => {
        gsap.to(batch, {
          scale: 1,
          autoAlpha: 1,
          duration: isMobile ? 0.52 : 0.95,
          ease: 'power3.out',
          stagger: isMobile ? 0.025 : 0.04,
          overwrite: 'auto',
        });
      },
    });
  };

  const initHeader = (gsap, ScrollTrigger) => {
    const headerGroup = document.querySelector('#header-group');
    if (!headerGroup) return;

    ScrollTrigger.create({
      id: `${STORMCREW_TRIGGER_PREFIX}header-state`,
      start: 32,
      end: 'max',
      onUpdate: (self) => {
        headerGroup.classList.toggle('stormcrew-gsap-scrolled', self.scroll() > 32);
      },
    });
  };

  const initHover = (gsap) => {
    if (!window.matchMedia('(pointer: fine)').matches) return;

    const hoverCards = uniqueElements(selectors.hoverCards);
    const buttons = uniqueElements(selectors.buttons);

    hoverCards.forEach((card) => {
      const image = card.querySelector('img');
      const label = card.querySelector('.sc-path-card__eyebrow, .sc-hero-card span, .sc-image-tile span, h3, .collection-title');
      const imageXTo = image ? gsap.quickTo(image, 'x', { duration: 0.45, ease: 'power3.out' }) : null;
      const imageYTo = image ? gsap.quickTo(image, 'y', { duration: 0.45, ease: 'power3.out' }) : null;
      const labelYTo = label ? gsap.quickTo(label, 'y', { duration: 0.35, ease: 'power3.out' }) : null;

      const enter = () => {
        gsap.to(card, { y: -4, duration: 0.28, ease: 'power2.out', overwrite: 'auto' });
        if (image) {
          gsap.to(image, { scale: 1.04, duration: 0.5, ease: 'power3.out', overwrite: 'auto' });
        }
        labelYTo?.(-2);
      };
      const move = (event) => {
        if (!imageXTo || !imageYTo) return;
        const rect = card.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        imageXTo(x * 10);
        imageYTo(y * 8);
      };
      const leave = () => {
        gsap.to(card, { y: 0, duration: 0.32, ease: 'power2.out', overwrite: 'auto' });
        if (image) {
          gsap.to(image, { x: 0, y: 0, scale: 1, duration: 0.48, ease: 'power3.out', overwrite: 'auto' });
        }
        labelYTo?.(0);
      };

      card.addEventListener('pointerenter', enter);
      card.addEventListener('pointermove', move);
      card.addEventListener('pointerleave', leave);
      cleanupHandlers.push(() => {
        card.removeEventListener('pointerenter', enter);
        card.removeEventListener('pointermove', move);
        card.removeEventListener('pointerleave', leave);
      });
    });

    buttons.forEach((button) => {
      const xTo = gsap.quickTo(button, 'x', { duration: 0.3, ease: 'power3.out' });
      const yTo = gsap.quickTo(button, 'y', { duration: 0.3, ease: 'power3.out' });
      const enter = () => gsap.to(button, { scale: 1.015, duration: 0.2, ease: 'power2.out', overwrite: 'auto' });
      const move = (event) => {
        const rect = button.getBoundingClientRect();
        xTo(((event.clientX - rect.left) / rect.width - 0.5) * 6);
        yTo(((event.clientY - rect.top) / rect.height - 0.5) * 4 - 1);
      };
      const leave = () =>
        gsap.to(button, { x: 0, y: 0, scale: 1, duration: 0.24, ease: 'power2.out', overwrite: 'auto' });

      button.addEventListener('pointerenter', enter);
      button.addEventListener('pointermove', move);
      button.addEventListener('pointerleave', leave);
      cleanupHandlers.push(() => {
        button.removeEventListener('pointerenter', enter);
        button.removeEventListener('pointermove', move);
        button.removeEventListener('pointerleave', leave);
      });
    });
  };

  const initHeroParallax = (gsap, ScrollTrigger, isMobile) => {
    const hero = document.querySelector('.sc-hero');
    const heroMedia = document.querySelector('.sc-hero__media');

    if (heroMedia && !isMobile) {
      gsap.to(heroMedia, {
        yPercent: -2.5,
        ease: 'none',
        scrollTrigger: {
          id: `${STORMCREW_TRIGGER_PREFIX}hero-parallax`,
          trigger: hero || heroMedia,
          start: 'top top',
          end: 'bottom top',
          scrub: 1.15,
        },
      });
    }

    if (!hero || isMobile || !window.matchMedia('(pointer: fine)').matches) return;

    const parallaxTargets = uniqueElements('.sc-hero-card', hero).slice(0, 8);
    if (!parallaxTargets.length) return;

    const movers = parallaxTargets.map((element, index) => ({
      xTo: gsap.quickTo(element, 'x', { duration: 0.55, ease: 'power3.out' }),
      yTo: gsap.quickTo(element, 'y', { duration: 0.55, ease: 'power3.out' }),
      depth: 4 + index * 1.25,
    }));

    const move = (event) => {
      const rect = hero.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;

      movers.forEach((mover) => {
        mover.xTo(x * mover.depth * 2);
        mover.yTo(y * mover.depth);
      });
    };

    const leave = () => {
      movers.forEach((mover) => {
        mover.xTo(0);
        mover.yTo(0);
      });
    };

    hero.addEventListener('pointermove', move);
    hero.addEventListener('pointerleave', leave);
    cleanupHandlers.push(() => {
      hero.removeEventListener('pointermove', move);
      hero.removeEventListener('pointerleave', leave);
      gsap.set(parallaxTargets, { clearProps: 'transform' });
    });
  };

  const initImageScroll = (gsap, ScrollTrigger, isMobile) => {
    if (isMobile) return;

    uniqueElements(selectors.images)
      .slice(0, 24)
      .forEach((image, index) => {
        gsap.fromTo(
          image,
          { yPercent: 1.5 },
          {
            yPercent: -1.5,
            ease: 'none',
            scrollTrigger: {
              id: `${STORMCREW_TRIGGER_PREFIX}image-${index}`,
              trigger: image,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 1.25,
            },
          }
        );
      });
  };

  const initHorizontalRails = (gsap, isMobile) => {
    const rails = uniqueElements(selectors.rails).filter((rail) => getMaxScrollLeft(rail) > 8);
    if (!rails.length) return;

    rails.forEach((rail) => {
      rail.classList.add('stormcrew-gsap-rail');

      let startX = 0;
      let startScrollLeft = 0;
      let lastX = 0;
      let lastTime = 0;
      let velocity = 0;
      let dragged = false;
      let pointerId = null;

      const onWheel = (event) => {
        if (isMobile || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
        if (getMaxScrollLeft(rail) <= 0) return;

        event.preventDefault();
        animateScrollLeft(gsap, rail, rail.scrollLeft + event.deltaY * 0.86, {
          duration: 0.55,
          ease: 'power3.out',
        });
      };

      const onPointerDown = (event) => {
        if (isMobile || event.button !== 0 || event.target.closest('button, input, textarea, select, summary')) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScrollLeft = rail.scrollLeft;
        lastX = event.clientX;
        lastTime = performance.now();
        velocity = 0;
        dragged = false;
        gsap.killTweensOf(rail, 'scrollLeft');
        rail.setPointerCapture?.(pointerId);
      };

      const onPointerMove = (event) => {
        if (pointerId !== event.pointerId) return;
        const now = performance.now();
        const deltaX = event.clientX - startX;
        const frameDelta = event.clientX - lastX;
        const frameTime = Math.max(16, now - lastTime);

        if (Math.abs(deltaX) > 4) {
          dragged = true;
          rail.classList.add('is-dragging');
        }

        if (dragged) {
          rail.scrollLeft = clamp(startScrollLeft - deltaX, 0, getMaxScrollLeft(rail));
          velocity = frameDelta / frameTime;
        }

        lastX = event.clientX;
        lastTime = now;
      };

      const onPointerUp = (event) => {
        if (pointerId !== event.pointerId) return;
        pointerId = null;
        rail.releasePointerCapture?.(event.pointerId);
        rail.classList.remove('is-dragging');

        if (dragged) {
          const target = rail.scrollLeft - velocity * 260;
          animateScrollLeft(gsap, rail, target, { duration: 0.62, ease: 'power3.out' });
        }

        window.setTimeout(() => {
          dragged = false;
        }, 0);
      };

      const onClick = (event) => {
        if (!dragged) return;
        event.preventDefault();
        event.stopPropagation();
      };

      rail.addEventListener('wheel', onWheel, { passive: false });
      rail.addEventListener('pointerdown', onPointerDown);
      rail.addEventListener('pointermove', onPointerMove);
      rail.addEventListener('pointerup', onPointerUp);
      rail.addEventListener('pointercancel', onPointerUp);
      rail.addEventListener('click', onClick, true);

      cleanupHandlers.push(() => {
        gsap.killTweensOf(rail);
        rail.classList.remove('stormcrew-gsap-rail', 'is-dragging');
        rail.removeEventListener('wheel', onWheel);
        rail.removeEventListener('pointerdown', onPointerDown);
        rail.removeEventListener('pointermove', onPointerMove);
        rail.removeEventListener('pointerup', onPointerUp);
        rail.removeEventListener('pointercancel', onPointerUp);
        rail.removeEventListener('click', onClick, true);
      });
    });
  };

  const initFooterAccordions = (gsap) => {
    const detailsItems = uniqueElements('footer .menu--accordion .menu__details, #footer-group .menu--accordion .menu__details');
    if (!detailsItems.length) return;

    detailsItems.forEach((details) => {
      const summary = details.querySelector('summary');
      const content = details.querySelector('.details-content');
      if (!summary || !content) return;

      const open = () => {
        gsap.killTweensOf(content);
        details.open = true;
        gsap.fromTo(
          content,
          { height: 0, autoAlpha: 0, overflow: 'hidden' },
          {
            height: 'auto',
            autoAlpha: 1,
            duration: 0.34,
            ease: 'power2.out',
            clearProps: 'height,overflow,opacity,visibility',
          }
        );
      };

      const close = () => {
        gsap.killTweensOf(content);
        gsap.fromTo(
          content,
          { height: content.offsetHeight, autoAlpha: 1, overflow: 'hidden' },
          {
            height: 0,
            autoAlpha: 0,
            duration: 0.26,
            ease: 'power2.inOut',
            onComplete: () => {
              details.open = false;
              gsap.set(content, { clearProps: 'height,overflow,opacity,visibility' });
            },
          }
        );
      };

      const onClick = (event) => {
        event.preventDefault();
        if (details.open) {
          close();
        } else {
          open();
        }
      };

      summary.addEventListener('click', onClick);
      cleanupHandlers.push(() => {
        summary.removeEventListener('click', onClick);
        gsap.killTweensOf(content);
        gsap.set(content, { clearProps: 'height,overflow,opacity,visibility' });
      });
    });
  };

  const findRelatedBenefitStrip = (paths) => {
    const pathsRoot = paths.closest('.stormcrew-aurelle-home') || paths;
    let sibling = pathsRoot.previousElementSibling;

    while (sibling) {
      const strip = sibling.matches?.('.sc-benefit-strip')
        ? sibling
        : sibling.querySelector?.('.sc-benefit-strip');
      if (strip) return strip;
      sibling = sibling.previousElementSibling;
    }

    return document.querySelector('.sc-benefit-strip');
  };

  const initPromisePathSync = (gsap) => {
    const pathGroups = uniqueElements('[data-sc-paths]');
    if (!pathGroups.length) return;

    pathGroups.forEach((paths) => {
      const benefitStrip = findRelatedBenefitStrip(paths);
      if (!benefitStrip) return;

      const cards = Array.from(paths.querySelectorAll('[data-sc-path-index]'));
      const promises = Array.from(benefitStrip.querySelectorAll('[data-sc-promise-index]'));
      if (!cards.length || !promises.length) return;

      let frame = 0;
      let syncing = false;
      const maxSyncIndex = Math.max(0, Math.min(cards.length, promises.length) - 1);

      const setActive = (index) => {
        const activeIndex = Math.max(0, Math.min(index, maxSyncIndex));
        benefitStrip.classList.add('is-syncing');
        promises.forEach((promise, promiseIndex) => {
          promise.classList.toggle('is-active', promiseIndex === activeIndex);
        });
        cards.forEach((card, cardIndex) => {
          card.classList.toggle('is-active', cardIndex === activeIndex);
        });
      };

      const getIndexFromScroll = (element, item) => {
        const width = item?.getBoundingClientRect().width || 1;
        return Math.max(0, Math.min(cards.length - 1, Math.round(element.scrollLeft / width)));
      };

      const syncTo = (source, target, item, smooth = false) => {
        const maxLeft = getMaxScrollLeft(target);
        const left = clamp(source.scrollLeft, 0, maxLeft);
        syncing = true;
        if (gsap) {
          animateScrollLeft(gsap, target, left, {
            duration: smooth ? 0.42 : 0.24,
            ease: smooth ? 'power3.out' : 'power2.out',
          });
          window.setTimeout(() => {
            syncing = false;
          }, smooth ? 430 : 250);
        } else if (smooth && target.scrollTo) {
          target.scrollTo({ left, behavior: 'smooth' });
          window.requestAnimationFrame(() => {
            syncing = false;
          });
        } else {
          target.scrollLeft = left;
          window.requestAnimationFrame(() => {
            syncing = false;
          });
        }
        setActive(getIndexFromScroll(source, item));
      };

      const onPathsScroll = () => {
        if (syncing) return;
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => syncTo(paths, benefitStrip, cards[0]));
      };

      const onBenefitScroll = () => {
        if (syncing) return;
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => syncTo(benefitStrip, paths, promises[0]));
      };

      const activateCard = (card) => {
        const index = Math.max(0, Math.min(Number(card.dataset.scPathIndex || 0), maxSyncIndex));
        const targetPromise = promises[index];
        setActive(index);
        if (targetPromise) {
          animateScrollLeft(gsap, benefitStrip, targetPromise.offsetLeft, { duration: 0.44, ease: 'power3.out' });
        }
      };

      paths.addEventListener('scroll', onPathsScroll, { passive: true });
      benefitStrip.addEventListener('scroll', onBenefitScroll, { passive: true });

      cards.forEach((card) => {
        const activate = () => activateCard(card);
        card.addEventListener('pointerenter', activate);
        card.addEventListener('focus', activate);
        cleanupHandlers.push(() => {
          card.removeEventListener('pointerenter', activate);
          card.removeEventListener('focus', activate);
        });
      });

      setActive(getIndexFromScroll(paths, cards[0]));

      cleanupHandlers.push(() => {
        if (gsap) {
          gsap.killTweensOf(paths);
          gsap.killTweensOf(benefitStrip);
        }
        window.cancelAnimationFrame(frame);
        paths.removeEventListener('scroll', onPathsScroll);
        benefitStrip.removeEventListener('scroll', onBenefitScroll);
        benefitStrip.classList.remove('is-syncing');
        promises.forEach((promise) => promise.classList.remove('is-active'));
        cards.forEach((card) => card.classList.remove('is-active'));
      });
    });
  };

  const initialize = () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      cleanup();
      initPromisePathSync();
      return;
    }

    if (window.Shopify?.designMode) {
      cleanup();
      initPromisePathSync();
      document.documentElement.classList.add('stormcrew-theme-editor');
      return;
    }

    ensureGsap()
      .then(() => {
        const { gsap, ScrollTrigger } = window;
        gsap.registerPlugin(ScrollTrigger);
        cleanup();
        initPromisePathSync(gsap);
        gsap.defaults({ duration: 0.65, ease: 'power3.out' });

        matchMediaContext = gsap.matchMedia();
        matchMediaContext.add(
          {
            isDesktop: '(min-width: 750px)',
            isMobile: '(max-width: 749px)',
            reduceMotion: '(prefers-reduced-motion: reduce)',
          },
          (context) => {
            const { isMobile, reduceMotion } = context.conditions;
            if (reduceMotion) return;

            initHeader(gsap, ScrollTrigger);
            initSpotTextReveal(gsap, ScrollTrigger, isMobile);
            initReveal(gsap, ScrollTrigger, isMobile);
            initSpotImageReveal(gsap, ScrollTrigger, isMobile);
            initHover(gsap);
            initHeroParallax(gsap, ScrollTrigger, isMobile);
            initImageScroll(gsap, ScrollTrigger, isMobile);
            initHorizontalRails(gsap, isMobile);
            initFooterAccordions(gsap);

            requestAnimationFrame(() => ScrollTrigger.refresh());
          }
        );
      })
      .catch(() => {
        document.documentElement.classList.add('stormcrew-gsap-unavailable');
      });
  };

  const scheduleInit = () => {
    window.clearTimeout(initTimer);

    if (window.Shopify?.designMode) {
      initTimer = window.setTimeout(initialize, 80);
      return;
    }

    const start = () => {
      const run = () => {
        initTimer = window.setTimeout(initialize, 700);
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 2400 });
      } else {
        run();
      }
    };

    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit, { once: true });
  } else {
    scheduleInit();
  }

  document.addEventListener('shopify:section:load', scheduleInit);
  document.addEventListener('shopify:section:unload', scheduleInit);
  document.addEventListener('shopify:block:select', () => window.ScrollTrigger?.refresh());
  window.addEventListener('load', () => window.ScrollTrigger?.refresh(), { once: true });

  window.StormcrewGSAP = {
    refresh: scheduleInit,
    cleanup,
  };
})();
